/**
 * Health Span Profile Model
 * Maps API response to UI-friendly format
 *
 * Source: GET /health-span-profile/health-profile
 * Sample response: src/services/healthWellness/healthSpan.health-profile.sample.json
 *
 * API Category → UI Category mapping:
 * - Lifestyle → nutrition
 * - MetabolicHealth → metabolism
 * - PhysicalFitness → exercise
 * - MentalEmotionalHealth → stress
 * - GutHealth → gut
 * - SkinHealth → skin
 *
 * score_by_sub_category keys (API) are kept as-is on scoreDetails[uiCategory].subCategories
 * Landing UI key mapping lives in assessment/healthSpanScore.utils.js
 */

const CATEGORY_MAPPING = {
  Lifestyle: 'nutrition',
  MetabolicHealth: 'metabolism',
  PhysicalFitness: 'exercise',
  MentalEmotionalHealth: 'stress',
  GutHealth: 'gut',
  SkinHealth: 'skin',
};

export class HealthSpanProfile {
  constructor(data) {
    const healthProfile = data?.health_profile || {};
    
    this.profileId = healthProfile.profile_id || null;
    this.totalScore = Math.round(healthProfile.total_score || 0);
    this.categoryScores = this.mapCategoryScores(healthProfile.score_by_category);
    this.scoreDetails = this.mapScoreDetails(healthProfile.score_details);
    this.createdAt = healthProfile.created_at || null;
    this.updatedAt = healthProfile.updated_at || null;
    this.generatedAt = data?.generated_at || null;
  }

  /**
   * Map API score_by_category to UI category IDs
   * @param {Object} scoreByCategory - API score_by_category object
   * @returns {Object} - Mapped category scores
   */
  mapCategoryScores(scoreByCategory = {}) {
    const mapped = {};
    
    Object.entries(CATEGORY_MAPPING).forEach(([apiKey, uiKey]) => {
      const score = scoreByCategory[apiKey];
      mapped[uiKey] = score !== undefined ? Math.round(score) : null;
    });
    
    return mapped;
  }

  /**
   * Map API score_details to structured format
   * @param {Array} scoreDetails - API score_details array
   * @returns {Object} - Mapped score details by category
   */
  mapScoreDetails(scoreDetails = []) {
    const mapped = {};
    
    scoreDetails.forEach((detail) => {
      const uiCategory = CATEGORY_MAPPING[detail.category];
      if (uiCategory) {
        mapped[uiCategory] = {
          id: detail._id,
          category: uiCategory,
          score: Math.round(detail.score || 0),
          subCategories: this.mapSubCategories(detail.score_by_sub_category),
          details: detail.details || {},
          createdAt: detail.created_at,
          updatedAt: detail.updated_at,
        };
      }
    });
    
    return mapped;
  }

  /**
   * Map sub-category scores
   * @param {Object} subCategories - API score_by_sub_category object
   * @returns {Object} - Mapped sub-category scores (rounded)
   */
  mapSubCategories(subCategories = {}) {
    const mapped = {};
    
    Object.entries(subCategories).forEach(([key, value]) => {
      // HealthBehavior: ถ้าค่าเป็น 0 ให้ไม่เอาเข้า (ถือว่าไม่มีข้อมูล)
      if (key === 'HealthBehavior' && (value === 0 || value === null || value === undefined)) {
        return;
      }
      mapped[key] = Math.round(value || 0);
    });
    
    return mapped;
  }

  /**
   * Check if profile has valid data
   * @returns {boolean}
   */
  hasData() {
    return this.profileId !== null && this.totalScore > 0;
  }

  /**
   * Get score for a specific category
   * @param {string} categoryId - UI category ID (nutrition, metabolism, etc.)
   * @returns {number|null}
   */
  getCategoryScore(categoryId) {
    return this.categoryScores[categoryId] ?? null;
  }

  /**
   * Get detailed score for a specific category
   * @param {string} categoryId - UI category ID
   * @returns {Object|null}
   */
  getCategoryDetail(categoryId) {
    return this.scoreDetails[categoryId] ?? null;
  }
}

export default HealthSpanProfile;
